import { useState, useEffect } from 'react';
import { useSessionContext } from '@supabase/auth-helpers-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { WaitlistEntry, UnsubscribeFeedback } from './adminTypes';

export interface AdminLog {
  id: string;
  message: string;
  log_timestamp: string;
  level: string;
}

export function useWaitlistOperations(
  isAuthorized: boolean,
  isLoading: boolean,
  setNotification: (n: { message: string; type: 'error' | 'success' | 'warning' } | null) => void
) {
  const { session } = useSessionContext();
  const { logout } = useAuth();

  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showLogsInfo, setShowLogsInfo] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [selectedEntries, setSelectedEntries] = useState<Set<string>>(new Set());
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [resendingEmail, setResendingEmail] = useState<string | null>(null);
  const [logs, setLogs] = useState<AdminLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [feedback, setFeedback] = useState<UnsubscribeFeedback[]>([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [reasonCounts, setReasonCounts] = useState<{ [key: string]: number }>({});
  const [intradayCount, setIntradayCount] = useState(0);

  useEffect(() => {
    if (isAuthorized && !isLoading) {
      fetchEntries();
      fetchLogs();
      fetchFeedback();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthorized, isLoading]);

  const fetchEntries = async () => {
    try {
      setLoading(true);
      if (!session?.user) throw new Error('Admin authentication required. Please log in.');

      const { data, error } = await supabase.rpc('admin_get_waitlist_entries');
      if (error) {
        if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
          throw new Error('Network connection failed. Please check your internet connection and try again.');
        }
        if (error.message.includes('Access denied')) {
          throw new Error('Access denied. Please ensure you are logged in as admin.');
        }
        if (error.message.includes('function admin_get_waitlist_entries() does not exist')) {
          throw new Error('Admin functions not deployed. Please contact support.');
        }
        throw new Error(`Database error: ${error.message}`);
      }

      const entriesData = Array.isArray(data) ? data : [];
      setEntries(entriesData);

      const today = new Date().toDateString();
      const todayCount = entriesData.filter((entry: WaitlistEntry) =>
        new Date(entry.created_at).toDateString() === today
      ).length;
      setIntradayCount(todayCount);
    } catch (err) {
      console.error('Fetch entries error:', err);
      let errorMessage = 'Failed to fetch data';
      if (err instanceof TypeError && err.message.includes('Failed to fetch')) {
        errorMessage = 'Network error: Unable to connect to database. Please check your connection and try again.';
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setNotification({ message: errorMessage, type: 'error' });
      setTimeout(() => setNotification(null), 5000);
      if (err instanceof Error && err.message.includes('authentication')) {
        await logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const deleteEntry = async (id: string) => {
    try {
      if (!session?.user) throw new Error('Admin authentication required');
      const { data, error } = await supabase.rpc('admin_delete_waitlist_entry', { entry_id: id });
      if (error) throw new Error(`Failed to delete entry: ${error.message}`);
      if (data && !data.success) throw new Error(data.error || 'Failed to delete entry');
      setEntries(entries.filter(entry => entry.id !== id));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Delete entry error:', err);
      setNotification({ message: err instanceof Error ? err.message : 'Failed to delete entry', type: 'error' });
      setTimeout(() => setNotification(null), 5000);
    }
  };

  const bulkDeleteEntries = async () => {
    if (selectedEntries.size === 0) return;
    try {
      if (!session?.user) throw new Error('Admin authentication required');
      const deletePromises = Array.from(selectedEntries).map(async (id) => {
        const { data, error } = await supabase.rpc('admin_delete_waitlist_entry', { entry_id: id });
        if (error) throw new Error(`Failed to delete entry: ${error.message}`);
        if (data && !data.success) throw new Error(data.error || 'Failed to delete entry');
        return id;
      });
      const deletedIds = await Promise.all(deletePromises);
      setEntries(entries.filter(entry => !deletedIds.includes(entry.id)));
      setSelectedEntries(new Set());
      setBulkDeleteConfirm(false);
    } catch (err) {
      console.error('Bulk delete error:', err);
      setNotification({ message: err instanceof Error ? err.message : 'Failed to delete entries', type: 'error' });
      setTimeout(() => setNotification(null), 5000);
    }
  };

  const toggleEntrySelection = (id: string) => {
    const newSelected = new Set(selectedEntries);
    if (newSelected.has(id)) newSelected.delete(id);
    else newSelected.add(id);
    setSelectedEntries(newSelected);
  };

  const selectAllEntries = () => {
    if (selectedEntries.size === entries.length) setSelectedEntries(new Set());
    else setSelectedEntries(new Set(entries.map(entry => entry.id)));
  };

  const resendWelcomeEmail = async (entry: WaitlistEntry) => {
    try {
      setResendingEmail(entry.id);
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) {
        setNotification({ message: 'Sign in again to resend welcome emails.', type: 'error' });
        setTimeout(() => setNotification(null), 5000);
        return;
      }
      const response = await fetch('/.netlify/functions/send-welcome-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ entryId: entry.id }),
      });
      if (!response.ok) {
        setNotification({ message: `Resend failed (status ${response.status}).`, type: 'error' });
        setTimeout(() => setNotification(null), 5000);
        return;
      }
      const result = await response.json();
      if (!result.success) {
        setNotification({ message: result.error || 'Failed to resend welcome email', type: 'error' });
        setTimeout(() => setNotification(null), 5000);
        return;
      }
      setNotification({ message: 'Welcome email resent.', type: 'success' });
      setTimeout(() => setNotification(null), 3000);
    } catch (err) {
      console.error('Resend email error:', err);
      setNotification({ message: err instanceof Error ? err.message : 'Failed to resend welcome email', type: 'error' });
      setTimeout(() => setNotification(null), 5000);
    } finally {
      setResendingEmail(null);
    }
  };

  const exportToCSV = () => {
    try {
      const headers = ['Name', 'Email', 'City', 'State', 'Motorcycle Type', 'Source', 'Created At'];
      const csvContent = [
        headers.join(','),
        ...entries.map(entry => [
          `"${entry.name}"`, `"${entry.email}"`, `"${entry.city || ''}"`,
          `"${entry.state || ''}"`, `"${entry.motorcycle_type}"`,
          `"${entry.source}"`, `"${new Date(entry.created_at).toLocaleString()}"`
        ].join(','))
      ].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ridepulse-waitlist-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      setNotification({ message: `CSV exported successfully (${entries.length} entries)`, type: 'success' });
      setTimeout(() => setNotification(null), 3000);
    } catch (err) {
      console.error('CSV export error:', err);
      setNotification({ message: err instanceof Error ? err.message : 'Failed to export CSV', type: 'error' });
      setTimeout(() => setNotification(null), 5000);
    }
  };

  const fetchLogs = async () => {
    try {
      setLogsLoading(true);
      const { data, error } = await supabase.rpc('admin_get_logs', { limit_count: 50 });
      if (error) console.error('Logs fetch error:', error);
      if (data) setLogs(data);
    } catch (err) {
      console.error('Fetch logs error:', err);
    } finally {
      setLogsLoading(false);
    }
  }; // eslint-disable-line react-hooks/exhaustive-deps

  const fetchFeedback = async () => {
    try {
      setFeedbackLoading(true);
      if (!session?.user) throw new Error('Admin authentication required');
      const { data, error } = await supabase
        .from('unsubscribe_feedback')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);
      if (error) throw new Error(`Failed to fetch feedback: ${error.message}`);
      if (data) {
        setFeedback(data);
        const counts: { [key: string]: number } = {};
        data.forEach((item: UnsubscribeFeedback) => {
          if (item.reason) counts[item.reason] = (counts[item.reason] || 0) + 1;
        });
        setReasonCounts(counts);
      }
    } catch (err) {
      console.error('Fetch feedback error:', err);
      if (err instanceof Error && err.message.includes('authentication')) await logout();
    } finally {
      setFeedbackLoading(false);
    }
  };

  const formatDate = (dateString: string) => new Date(dateString).toLocaleString();

  return {
    entries,
    loading,
    showLogsInfo,
    setShowLogsInfo,
    deleteConfirm,
    setDeleteConfirm,
    selectedEntries,
    setSelectedEntries,
    bulkDeleteConfirm,
    setBulkDeleteConfirm,
    resendingEmail,
    logs,
    logsLoading,
    feedback,
    feedbackLoading,
    reasonCounts,
    intradayCount,
    fetchEntries,
    deleteEntry,
    bulkDeleteEntries,
    toggleEntrySelection,
    selectAllEntries,
    resendWelcomeEmail,
    exportToCSV,
    fetchLogs,
    fetchFeedback,
    formatDate,
  };
}
